import './styles.scss'

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { Col, Row } from 'antd'

import B2becPagination from '@/components/B2becPagination/B2becPagination'
import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon, ListViewIcon, TileViewIcon } from '@/components/Icons'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectAllResultAmount,
  selectAllResultList,
  selectIsAllResultLoading,
} from '@/store/selectors/searchSelector'
import { trackOnSiteSearch } from '@/store/slices/adobeAnalyticSlice'
import { addProductToCart } from '@/store/slices/cartSlice'
import { requestSearch } from '@/store/slices/searchSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { ALL_SEARCH_RESULTS_PER_TYPE, CATEGORY_TYPE } from '@/libs/constants'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { linkGenerator } from '@/libs/utils/language'

import { GridView, ListView } from '../components/ResultViewList'

function SearchResultsDetailPage({ breadcrumbs }) {
  const { searchParam, type } = useParams()

  useDocumentTitle('pageTitle.searchResults')
  useBreadcrumbs(breadcrumbs, [{ pathData: [type, searchParam] }])

  const { setPageInfoData } = useAdobeAnalysis(TRACK_DATA.ONSITE)

  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [currentPage, setCurrentPage] = useState(1)
  // TODO: Uncomment when sorting functionality is needed
  // const [sortOption, setSortOption] = useState(sortingOptions[0]);

  const [showListView, setShowListView] = useState(false)

  const getTypeKeyByName = useCallback((typeName) => {
    // value of each category in CATEGORY_TYPE is an array.
    // Ex: [accessory, accessories]
    return Object.keys(CATEGORY_TYPE).find(
      (typeKey) => CATEGORY_TYPE[typeKey][1] === typeName,
    )
  }, [])

  const isProductLoading = useAppSelector(selectIsAllResultLoading)
  const productsList = useAppSelector(selectAllResultList)
  const productAllAmount = useAppSelector(selectAllResultAmount)

  const onAddProductToCart = useCallback(
    (materialNumber) => {
      dispatch(addProductToCart({ materialNumber }))
    },
    [dispatch],
  )

  useEffect(() => {
    if (searchParam && type) {
      dispatch(
        requestSearch({
          searchAll: true,
          value: searchParam,
          size: ALL_SEARCH_RESULTS_PER_TYPE,
          from: ALL_SEARCH_RESULTS_PER_TYPE * (currentPage - 1),
          category: getTypeKeyByName(type),
          // sortBy: sortOption.sortOrder - 1,
        }),
      )

      setPageInfoData({
        pageName: `${type}SearchResults`,
        pageType: PAGE_TYPES.SEARCH_RESULTS,
        pageId: '15',
      })
      dispatch(
        trackOnSiteSearch({
          onsiteSearchTerm: searchParam,
          onsiteSearchResult: productAllAmount,
        }),
      )
    }
  }, [
    dispatch,
    searchParam,
    productAllAmount,
    currentPage,
    type,
    setPageInfoData,
    getTypeKeyByName,
  ])

  const onChangePaginateHandler = (newPageNumber) => {
    window.scrollTo(0, 0)
    setCurrentPage(newPageNumber)
  }

  // TODO: Uncomment when sorting functionality is needed
  // const handleSort = (value) => {
  //   if (currentPage !== 1) {
  //     setCurrentPage(1);
  //   }
  //   setSortOption(value);
  // };

  return isProductLoading && !showListView
    ? (
      <B2becCardSkeleton
        gutter={20}
        columns={{ xs: 24, sm: 24, md: 12, lg: 8, xl: 8, xxl: 8 }}
      />
    )
    : (
      <div className="container-full-width-page">
        <div className="search-results">
          <div className="search-results__header">
            <div className="search-results__header__title">
              {t('searchResultsPage.title', {
                count: productAllAmount,
              }).trim()} <q>{searchParam}</q> {t(`searchResultsPage.header.${type}`)}
            </div>

            <Row>
              <Col lg={12} md={12} sm={12} xl={12} xs={18}>
                <CustomButton
                  buttonSize="small"
                  className="back-btn"
                  onClick={() => {
                    navigate(linkGenerator(`/search-results/${searchParam}`))
                  }}
                >
                  <ChevronLeftIcon />
                  {t('searchResultsPage.button.backToOverview')}
                </CustomButton>
              </Col>
              <Col
                lg={12}
                md={12}
                sm={12}
                xl={12}
                xs={6}
                style={{ textAlign: 'right' }}
              >
                {
                  // TODO: Uncomment when sorting functionality is needed
                  //   <B2becSorting
                  //     onSort={handleSort}
                  //     options={sortingOptions}
                  //     sortOption={sortOption}
                  //   />
                }
                <span className="change-view-icons">
                  <TileViewIcon onClick={() => setShowListView(false)} />
                  <ListViewIcon onClick={() => setShowListView(true)} />
                </span>
              </Col>
            </Row>
          </div>
          <div className="search-results__content">
            {showListView
              ? (
                <ListView
                  dataSrc={productsList}
                  isLoading={isProductLoading}
                  onAddProductToCart={onAddProductToCart}
                />
              )
              : (
                <GridView
                  dispatch={dispatch}
                  productsList={productsList}
                  onAddProductToCart={onAddProductToCart}
                />
              )}

            <div className="page-pagination">
              <B2becPagination
                total={productAllAmount}
                current={currentPage}
                pageSize={ALL_SEARCH_RESULTS_PER_TYPE}
                onPageChange={(e) => onChangePaginateHandler(e)}
              />
            </div>
          </div>
        </div>
      </div>
    )
}

export default SearchResultsDetailPage
